<template>
  <div class="home">
    <div class="ls-hero ls-card" :class="{ 'ls-hero-compact': isSuper && user.loggedIn }">
      <div class="ls-hero-mark">Ls</div>
      <h1>{{ greeting }}</h1>
      <p class="ls-hero-sub">
        Leerlingensync spiegelt de leerlingen uit <b>Informat</b> naar <b>Google&nbsp;Workspace</b> —
        accounts, organisatie-eenheden, discussiegroepen en classrooms.
      </p>
      <div v-if="!user.loggedIn" class="ls-hero-cta">
        <router-link to="/login" class="ls-btn ls-primary">
          <span class="material-icons" style="font-size:17px">login</span> Aanmelden met Google
        </router-link>
      </div>
    </div>

    <!-- Live cijfers (superuser) -->
    <template v-if="user.loggedIn && isSuper">
      <section class="ls-stats">
        <router-link to="/joboverview" class="ls-stat ls-statlink">
          <div class="ls-lbl">In wachtrij</div>
          <div class="ls-num"><span class="ls-dot" style="background:var(--ls-warn)"></span>{{ jobsPending }}</div>
        </router-link>
        <router-link to="/joboverview" class="ls-stat ls-statlink">
          <div class="ls-lbl">Mislukte jobs</div>
          <div class="ls-num"><span class="ls-dot" style="background:var(--ls-err)"></span>{{ jobsFailed }}</div>
        </router-link>
        <router-link to="/erroroverview" class="ls-stat ls-statlink">
          <div class="ls-lbl">Foutmeldingen</div>
          <div class="ls-num"><span class="ls-dot" :style="{ background: errors.length ? 'var(--ls-err)' : 'var(--ls-ok)' }"></span>{{ errors.length }}</div>
        </router-link>
        <router-link to="/cronjoberroroverview" class="ls-stat ls-statlink">
          <div class="ls-lbl">Cronjob-fouten</div>
          <div class="ls-num"><span class="ls-dot" :style="{ background: cronErrors.length ? 'var(--ls-err)' : 'var(--ls-ok)' }"></span>{{ cronErrors.length }}</div>
        </router-link>
      </section>
      <p class="ls-lastact" v-if="lastActivity">
        <span class="material-icons">schedule</span>
        Laatste sync-activiteit: <b class="ls-mono">{{ lastActivity }}</b>
      </p>
    </template>

    <!-- Leerlingen per school (opt-in) -->
    <section v-if="user.loggedIn" class="ls-card ls-countpanel">
      <div class="ls-countpanel-head">
        <span class="material-icons">storage</span>
        <b>Leerlingen per school</b>
        <button class="ls-btn ls-ghost ls-sm" style="margin-left:auto" :disabled="loadingCounts" @click="loadCounts">
          <span class="material-icons" style="font-size:15px">refresh</span>
          {{ schoolCounts ? 'Vernieuw' : 'Laad tellingen' }}
        </button>
      </div>
      <div class="ls-countpanel-body">
        <p v-if="!schoolCounts && !loadingCounts" class="ls-countpanel-hint">
          Tellingen worden niet automatisch geladen (dat leest alle leerlingdocs). Klik “Laad tellingen”.
        </p>
        <div v-else-if="loadingCounts" class="ls-countpanel-hint">Tellen…</div>
        <div v-else class="ls-countgrid">
          <div v-for="row in schoolCounts" :key="row.school" class="ls-countcell">
            <span class="ls-mono">{{ row.school }}</span>
            <b>{{ row.count }}</b>
          </div>
          <div class="ls-countcell ls-countcell-total">
            <span>Totaal</span>
            <b>{{ totalPupils }}</b>
          </div>
        </div>
      </div>
    </section>

    <!-- Snelkoppelingen -->
    <div v-if="user.loggedIn" class="ls-quicklinks">
      <router-link v-if="isSuper" to="/manualsync" class="ls-ql ls-card">
        <span class="material-icons">cloud_sync</span><b>Manuele synchronisatie</b>
        <span class="ls-ql-note">Start een sync-stap met de hand</span>
      </router-link>
      <router-link to="/students" class="ls-ql ls-card">
        <span class="material-icons">storage</span><b>Leerlingen</b>
        <span class="ls-ql-note">Zoek en beheer een leerling</span>
      </router-link>
      <router-link v-if="isSuper" to="/gmailonlyorgs" class="ls-ql ls-card">
        <span class="material-icons">no_accounts</span><b>Gmail-only OU's</b>
        <span class="ls-ql-note">Duid classroom-loze OU's aan</span>
      </router-link>
      <router-link to="/info" class="ls-ql ls-card">
        <span class="material-icons">info</span><b>Info &amp; account</b>
        <span class="ls-ql-note">Je rechten en de jaarlijkse setup</span>
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as fb from '../firebase'
export default {
  name: 'HomeView',
  computed: {
    ...mapGetters({ user: 'user' }),
    isSuper () { return !!(this.user && this.user.claims && this.user.claims.superuser) },
    availableSchools () {
      const s = (this.user.claims && this.user.claims.schools) || {}
      return Object.keys(s).filter((k) => s[k] === true)
    },
    greeting () {
      if (!this.user.loggedIn) { return 'Welkom bij Leerlingensync' }
      const d = this.user.data
      const naam = ((d && (d.displayName || d.email)) || '').split(' ')[0]
      return naam ? ('Dag ' + naam) : 'Welkom terug'
    },
    jobsPending () { return this.jobs.filter((j) => !j.completed).length },
    jobsFailed () {
      return this.jobs.filter((j) => j.completed && !(j.result && j.result.human === 'GENEGEERD') && !(j.result && j.result.success)).length
    },
    lastActivity () {
      const times = this.jobs.map((j) => new Date(j.added).getTime()).filter((t) => !isNaN(t))
      if (times.length === 0) { return '' }
      return new Date(Math.max.apply(null, times)).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })
    },
    totalPupils () {
      return (this.schoolCounts || []).reduce((sum, r) => sum + r.count, 0)
    }
  },
  data () {
    return {
      jobs: [],
      errors: [],
      cronErrors: [],
      schoolCounts: null,
      loadingCounts: false
    }
  },
  mounted () {
    if (this.user.loggedIn && this.isSuper) { this.watchStats() }
  },
  methods: {
    watchStats () {
      const u1 = fb.jobCollection.onSnapshot((s) => { this.jobs = s.docs.map((d) => d.data()) })
      const u2 = fb.errorCollection.onSnapshot((s) => { this.errors = s.docs.map((d) => d.data()) })
      const u3 = fb.cronJobErrorsCollection.onSnapshot((s) => { this.cronErrors = s.docs.map((d) => d.data()) })
      this.$store.dispatch('addUnsubscriber', u1)
      this.$store.dispatch('addUnsubscriber', u2)
      this.$store.dispatch('addUnsubscriber', u3)
    },
    async loadCounts () {
      this.loadingCounts = true
      try {
        const rows = await Promise.all(this.availableSchools.map(async (school) => {
          const snap = await fb.db.collection(school).get()
          return { school, count: snap.size }
        }))
        this.schoolCounts = rows.sort((a, b) => a.school.localeCompare(b.school))
      } catch (e) {
        console.log(e)
        this.$toastr && this.$toastr.error(e, 'Fout bij het tellen!', this.$store.getters.toastrOptions)
      }
      this.loadingCounts = false
    }
  }
}
</script>

<style scoped>
.home { display: flex; flex-direction: column; gap: 18px; }
.ls-hero { padding: 40px 34px; text-align: center; }
.ls-hero.ls-hero-compact { padding: 26px 30px; }
.ls-hero-mark { width: 54px; height: 54px; border-radius: 13px; background: var(--ls-accent); color: var(--ls-accent-ink); display: grid; place-items: center; font-family: var(--ls-sans); font-weight: 700; font-size: 24px; margin: 0 auto 16px; }
.ls-hero.ls-hero-compact .ls-hero-mark { width: 42px; height: 42px; font-size: 19px; margin-bottom: 12px; }
.ls-hero h1 { font-family: var(--ls-sans); font-size: 30px; font-weight: 700; letter-spacing: -.6px; margin: 0 0 10px; }
.ls-hero.ls-hero-compact h1 { font-size: 24px; margin-bottom: 6px; }
.ls-hero-sub { max-width: 62ch; margin: 0 auto; color: var(--ls-ink-2); font-size: 15px; line-height: 1.6; }
.ls-hero-cta { margin-top: 22px; }

.ls-stats { margin: 0; }
.ls-statlink { text-decoration: none; color: var(--ls-ink); transition: transform .12s ease, box-shadow .12s ease; }
.ls-statlink:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(20,32,43,.1); }
.ls-lastact { display: flex; align-items: center; gap: 7px; font-size: 13px; color: var(--ls-ink-2); margin: -4px 0 0; }
.ls-lastact .material-icons { font-size: 17px; color: var(--ls-ink-3); }

.ls-countpanel { overflow: hidden; }
.ls-countpanel-head { display: flex; align-items: center; gap: 10px; padding: 13px 18px; border-bottom: 1px solid var(--ls-line); background: var(--ls-surface-2); }
.ls-countpanel-head .material-icons { color: var(--ls-primary); }
.ls-countpanel-head b { font-family: var(--ls-sans); font-size: 15px; }
.ls-countpanel-body { padding: 16px 18px; }
.ls-countpanel-hint { color: var(--ls-ink-3); font-size: 13px; margin: 0; }
.ls-countgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 10px; }
.ls-countcell { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; padding: 10px 12px; border: 1px solid var(--ls-line); border-radius: 8px; }
.ls-countcell span { font-size: 12.5px; color: var(--ls-ink-2); font-weight: 600; }
.ls-countcell b { font-family: var(--ls-mono); font-size: 18px; font-weight: 600; }
.ls-countcell-total { background: var(--ls-surface-2); border-color: var(--ls-line-strong); }
.ls-countcell-total b { color: var(--ls-primary); }

.ls-quicklinks { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.ls-ql { display: flex; flex-direction: column; gap: 4px; padding: 18px; text-decoration: none; color: var(--ls-ink); transition: transform .12s ease, box-shadow .12s ease; }
.ls-ql:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(20,32,43,.1); }
.ls-ql .material-icons { color: var(--ls-primary); font-size: 26px; margin-bottom: 4px; }
.ls-ql b { font-family: var(--ls-sans); font-size: 15px; }
.ls-ql-note { font-size: 12.5px; color: var(--ls-ink-3); }
</style>
